/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import * as stylex from '@stylexjs/stylex';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import type { ScaleInput } from '@visx/scale/lib/types/Scale';
import type { ScaleLinear } from '@visx/vendor/d3-scale';
import {
  Children,
  createContext,
  type ForwardedRef,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { type ContextValue, Provider, type SlotProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { Axis, AXIS_SLOTS, AxisContext } from '../axis/axis.component';
import { ChartCrosshairContext } from '../chart-crosshair/chart-crosshair.component';
import { ChartGridColumnsContext } from '../chart-grid-columns/chart-grid-columns.component';
import { ChartGridRowsContext } from '../chart-grid-rows/chart-grid-rows.component';
import { LinePathContext } from '../line-path/line-path.component';
import { SeriesContext } from '../series/series.component';

type LineChartDatum<T extends unknown[] | unknown[][]> = T extends (infer U)[]
  ? U extends unknown[]
    ? U[number]
    : U
  : never;

interface LineChartProps<
  T extends unknown[] | unknown[][] = unknown[] | unknown[][],
  XScale extends ScaleLinear<number, number> = ScaleLinear<number, number>,
  YScale extends ScaleLinear<number, number> = ScaleLinear<number, number>,
> extends SlotProps {
  data: T;
  xAccessor: (datum: LineChartDatum<T>) => ScaleInput<XScale>;
  yAccessor: (datum: LineChartDatum<T>) => ScaleInput<YScale>;
  'aria-label'?: string;
  height: number;
  style?: stylex.StyleXStyles;
  children?: ReactNode;
}

const LineChartContext = createContext<ContextValue<LineChartProps<unknown[] | unknown[][]>, HTMLDivElement>>({});

const LineChart = forwardRef((props: LineChartProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, LineChartContext);

  const { data, 'aria-label': ariaLabel, xAccessor, yAccessor, style, slot, children, height, ...coreProps } = props;

  const axisSlots = useMemo(() => collectAxisSlots(children), [children]);

  const [overlayContainer, setOverlayContainer] = useState<HTMLDivElement | undefined>(undefined);
  const [seriesRegistry, setSeriesRegistry] = useState<{ label?: string; color: string }[]>([]);

  const seriesData = useMemo(() => {
    const series = (Array.isArray(data[0]) ? data : [data]) as LineChartDatum<typeof data>[][];

    return series.map((seriesData) =>
      seriesData.map((datum) => ({
        x: Number(xAccessor(datum)),
        y: Number(yAccessor(datum)),
      })),
    );
  }, [data, xAccessor, yAccessor]);

  const [minX, maxX, minY, maxY] = useMemo(() => {
    const flatSeriesData = seriesData.flat();

    const xValues = flatSeriesData.map((datum) => datum.x);
    const yValues = flatSeriesData.map((datum) => datum.y);

    const minXValue = Math.min(...xValues);
    const maxXValue = Math.max(...xValues);
    const minYValue = Math.min(0, ...yValues);
    const maxYValue = Math.max(...yValues);

    return [minXValue, maxXValue, minYValue, maxYValue];
  }, [seriesData]);

  const xTickValues = useMemo(() => {
    const values = seriesData.flat().map((datum) => datum.x);

    const uniqueValues = Array.from(new Set(values));

    return uniqueValues.sort((a, b) => a - b);
  }, [seriesData]);

  return (
    <div ref={ref} slot={slot || undefined} {...stylex.props(styles.base(height), style)} {...coreProps}>
      <ParentSize debounceTime={0}>
        {({ width: parentWidth, height: parentHeight }) => {
          const margin = getDynamicMargins({
            baseMargin: { top: 24, right: 24, bottom: 32, left: 40 },
            axisSlots,
            minY,
            maxY,
          });

          const innerWidth = Math.max(parentWidth - margin.left - margin.right, 0);
          const innerHeight = Math.max(parentHeight - margin.top - margin.bottom, 0);

          const xScale = scaleLinear({
            domain: [minX, maxX],
            range: [0, innerWidth],
          });

          const yScale = scaleLinear({
            domain: [minY, maxY],
            range: [innerHeight, 0],
          });

          return (
            <Provider
              values={[
                [
                  AxisContext,
                  {
                    slots: {
                      [AXIS_SLOTS.LEFT]: {
                        scale: yScale,
                      },
                      [AXIS_SLOTS.RIGHT]: {
                        scale: yScale,
                        left: innerWidth,
                      },
                      [AXIS_SLOTS.TOP]: {
                        scale: xScale,
                        tickValues: xTickValues,
                      },
                      [AXIS_SLOTS.BOTTOM]: {
                        scale: xScale,
                        top: innerHeight,
                        tickValues: xTickValues,
                      },
                    },
                  },
                ],
                [
                  ChartGridColumnsContext,
                  {
                    scale: xScale,
                    height: innerHeight,
                    tickValues: xTickValues,
                  },
                ],
                [
                  ChartGridRowsContext,
                  {
                    scale: yScale,
                    width: innerWidth,
                  },
                ],
                [
                  LinePathContext,
                  {
                    data: seriesData[0] ?? [],
                    xScale,
                    yScale,
                    innerWidth,
                    innerHeight,
                  },
                ],
                [
                  SeriesContext,
                  {
                    data: seriesData,
                    colorScale: { domain: [], range: [] },
                    registerSeries: setSeriesRegistry,
                  },
                ],
                [
                  ChartCrosshairContext,
                  {
                    data: data,
                    xScale,
                    yScale,
                    xAccessor: (datum) => Number(xAccessor(datum)),
                    yAccessor: (datum) => Number(yAccessor(datum)),
                    innerWidth,
                    innerHeight,
                    margin,
                    overlayContainer,
                    series: seriesRegistry,
                  },
                ],
              ]}
            >
              <div
                ref={(node) => {
                  setOverlayContainer(node ?? undefined);
                }}
                {...stylex.props(styles.container)}
              >
                <svg
                  width={parentWidth}
                  height={parentHeight}
                  role="img"
                  aria-label={ariaLabel}
                  {...stylex.props(styles.chartSvg)}
                >
                  <g transform={`translate(${margin.left}, ${margin.top})`}>{children}</g>
                </svg>
              </div>
            </Provider>
          );
        }}
      </ParentSize>
    </div>
  );
}) as <
  T extends unknown[] | unknown[][] = unknown[] | unknown[][],
  XScale extends ScaleLinear<number, number> = ScaleLinear<number, number>,
  YScale extends ScaleLinear<number, number> = ScaleLinear<number, number>,
>(
  props: LineChartProps<T, XScale, YScale> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReactElement;

const styles = stylex.create({
  base: (height: number) => ({
    backgroundColor: $.surfaceBGPrimary,
    height: '100%',
    minHeight: height,
    width: '100%',
  }),
  container: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  chartSvg: {
    position: 'relative',
    zIndex: 1,
  },
});

const AXIS_TICK_LABEL_FONT_SIZE = 12;
const AXIS_TICK_LABEL_PADDING = 12;
const AXIS_TICK_LABEL_WIDTH_FACTOR = 0.6;

const estimateTextWidth = (text: string, fontSize: number) =>
  Math.ceil(text.length * fontSize * AXIS_TICK_LABEL_WIDTH_FACTOR);

const getFormattedTicks = (minY: number, maxY: number) => {
  const scale = scaleLinear({ domain: [minY, maxY], range: [0, 1] });
  const ticks = scale.ticks();
  const format = scale.tickFormat(ticks.length);

  return ticks.map((value) => {
    const formatted = format(value);
    return formatted == null ? '' : String(formatted);
  });
};

const getMaxTickLabelWidth = (minY: number, maxY: number) => {
  const labels = getFormattedTicks(minY, maxY);
  return labels.length ? Math.max(...labels.map((label) => estimateTextWidth(label, AXIS_TICK_LABEL_FONT_SIZE))) : 0;
};

const getDynamicMargins = ({
  baseMargin,
  axisSlots,
  minY,
  maxY,
}: {
  baseMargin: { top: number; right: number; bottom: number; left: number };
  axisSlots: Set<string>;
  minY: number;
  maxY: number;
}) => {
  const margin = { ...baseMargin };

  if (!axisSlots.has(AXIS_SLOTS.LEFT) && !axisSlots.has(AXIS_SLOTS.RIGHT)) {
    return margin;
  }

  const maxLabelWidth = getMaxTickLabelWidth(minY, maxY);
  if (maxLabelWidth === 0) {
    return margin;
  }

  const labelMargin = Math.ceil(maxLabelWidth + AXIS_TICK_LABEL_PADDING);

  if (axisSlots.has(AXIS_SLOTS.LEFT)) {
    margin.left = Math.max(margin.left, labelMargin);
  }

  if (axisSlots.has(AXIS_SLOTS.RIGHT)) {
    margin.right = Math.max(margin.right, labelMargin);
  }

  return margin;
};

const collectAxisSlots = (children: ReactNode) => {
  const slots = new Set<string>();

  const walk = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement<{ slot?: string; children?: ReactNode }>(child)) {
        return;
      }

      if (child.type === Axis) {
        const slot = child.props?.slot;
        if (typeof slot === 'string') {
          slots.add(slot);
        }
      }

      if (child.props?.children) {
        walk(child.props.children);
      }
    });
  };

  walk(children);

  return slots;
};

export { LineChart, LineChartContext, type LineChartProps };
